import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react'
import { connect, useDispatch, useSelector } from 'react-redux'
import { useNavigate, Link as RLink } from 'react-router-dom'
import TablePagination from './TablePagination.jsx'
import { Link, LinkStyle, Tag, styled } from '@pergas-common/pergas-components'
import { Table, Td } from '../../pergas-components/index.js'
import { Add, CircularTick, Favorite, FavoriteFilled } from '@pergas-common/pergas-icons'
import { makeSelectorByEmployeeId } from '../../redux/login/selectors'
import DeleteItem from '../dialogs/DeleteItem.js'
import { DeleteItemIcon } from '../icons.js'
import PageToolbar from './PageToolbar.js'
import SearchField from '../SearchField.js'
import { Switch } from '../Input'
import redux from '../../redux/index.js'
import storage from '../../redux/storage.js'
import { sortArray, trimNewLineFromString } from '../../util.js'
import { isExpired } from './columns.js'
import dayjs from 'dayjs'
import { makeSelectorFilterQueries } from '../../redux/common.js'
import CsvButton from '../CsvButton.js'
import FilterFavoriteButton from '../filter/FilterFavoriteButton'
import Filterbar from '../filter/Filterbar'
import FilterEmployee from '../filter/FilterEmployee'
import All from '../filter/FilterAll'
import FilterMine from '../filter/FilterMine'
import { selectLocale } from '../../redux/locale/selectors.js'
import FilterProject from '../filter/FilterProject.js'
import FilterNumber from '../filter/FilterNumber.js'
import { css } from 'styled-components'
import PageFooter from './PageFooter.jsx'
import Tooltip from '../Tooltip'
import ColumnMultiSelectFilter from '../filter/ColumnMultiSelectFilter.js'
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormLabel,
  FormGroup,
  IconButton,
  Popover,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'

const TAG_FILTER_KEY = 'tags_string'
const ASSIGNED_FILTER_KEY = 'assigned_person_name_string'
const TAG_FILTER_OP = '~'
const TAG_FILTER_MODE_OR = 'or'
const TAG_FILTER_MODE_AND = 'and'

const PageHolder = styled.div`
  display: flex;
  overflow: hidden;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`

const TableHolder = styled.div`
  display: flex;
  overflow-y: scroll;
  flex-grow: 1;
  flex-basis: 50px;
  min-height: 0;

  ${({ $isExpanded }) => $isExpanded && css`
    top: 220px;
  `};
`

const TagHolder = styled.span`
  margin-right: 8px;
`

const Spacer = styled.div`
  display: inline-block;
  margin: 2px;
`

const CircularTickHolder = styled(CircularTick)`
  vertical-align: bottom;
`

const NameHolder = styled.div`
  display: inline-block;
  margin: 0 8px;
`

const TagsHeaderHolder = styled.div`
  display: inline-flex;
  align-items: center;
`

const TagFilterBody = styled.div`
  width: 280px;
  padding: 8px;
`

const TagFilterActions = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 8px;
`

const ClearFiltersHolder = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 10px;
  line-height: 1;

  > div {
    display: inline-flex;
    align-items: center;
  }

  a {
    display: inline-flex;
    align-items: center;
  }

  svg {
    display: block;
  }
`

const InternalLink = styled(RLink)`
  ${LinkStyle}
`

const Strikethrough = styled.span`
  text-decoration: ${({ isDone }) => isDone ? 'line-through' : 'none'};
`

const FavoriteHolder = styled.div`
  margin-top: -2px;
`

const Expired = styled.span`
  color: ${({ date }) => isExpired(date)
? `
    #FF5656
  `
: 'inherit'};
`

const VALID_OPS_ESTIMATED = [
  { name: 'gt', op: '>' },
  { name: 'lt', op: '<' },
  { name: 'gteq', op: '>=' },
  { name: 'lteq', op: '<=' }
]

const FilterList = () => {
  const dispatch = useDispatch()
  const selectFilterQueries = useMemo(makeSelectorFilterQueries, [])
  const selectMyEmployeeId = useMemo(makeSelectorByEmployeeId, [])

  const filterQueries = useSelector((state) => selectFilterQueries(state, 'ticket'))
  const myEmployeeId = useSelector((state) => selectMyEmployeeId(state))
  const locale = useSelector(selectLocale)

  const filterByMine = filterQueries?.find(({ key, value }) => key === 'person_role.person_id' && value === myEmployeeId)
  const filterByAssignedTo = filterQueries?.find(({ key, value }) => key === 'person_role.person_id' && value !== myEmployeeId)
  const filterByAll = filterQueries.filter(({ key }) => key === 'person_role.person_id').length === 0
  const filterByProject = filterQueries?.find(({ key }) => key === 'project_id')
  const filterByContactResponsible = filterQueries?.find(({ key }) => key === 'collection_person_role.person_id')
  const filterByProjectResponsible = filterQueries?.find(({ key }) => key === 'project_person_role.person_id')
  const isMine = filterByMine && !filterByAll && !filterByAssignedTo
  const isAll = filterByAll && !isMine && !filterByAssignedTo
  const isOther = filterByAssignedTo && !isMine && !isAll

  const assignedToFilter = useCallback((id, metadata = {}) => {
    const included = filterQueries.filter(({ key }) => key !== 'person_role.person_id' && key !== 'person_role.role_id')
    if (!id) {
      dispatch(redux.actions.ticket.setFilterQueries(included))
    } else {
      dispatch(redux.actions.ticket.setFilterQueries([...included, { key: 'person_role.person_id', value: id, op: '=', ...metadata }, { key: 'person_role.role_id', value: 1, op: '=' }]))
    }
  }, [dispatch, filterQueries])

  const projectFilter = useCallback((id, metadata = {}) => {
    const included = filterQueries.filter(({ key }) => key !== 'project_id')
    if (!id) {
      dispatch(redux.actions.ticket.setFilterQueries(included))
    } else {
      dispatch(redux.actions.ticket.setFilterQueries([...included, { key: 'project_id', value: id, op: '=', ...metadata }]))
    }
  }, [dispatch, filterQueries])

  const projectResponsibleFilter = useCallback((id, metadata = {}) => {
    const included = filterQueries.filter(({ key }) => key !== 'project_person_role.person_id' && key !== 'project_person_role.role_id')
    if (!id) {
      dispatch(redux.actions.ticket.setFilterQueries(included))
    } else {
      dispatch(redux.actions.ticket.setFilterQueries([...included, { key: 'project_person_role.person_id', value: id, op: '=', ...metadata }, { key: 'project_person_role.role_id', value: 1, op: '=' }]))
    }
  }, [dispatch, filterQueries])

  const contactResponsibleFilter = useCallback((id, metadata = {}) => {
    const included = filterQueries.filter(({ key }) => key !== 'collection_person_role.person_id' && key !== 'collection_person_role.role_id')
    if (!id) {
      dispatch(redux.actions.ticket.setFilterQueries(included))
    } else {
      dispatch(redux.actions.ticket.setFilterQueries([...included, { key: 'collection_person_role.person_id', value: id, op: '=', ...metadata }, { key: 'collection_person_role.role_id', value: 1, op: '=' }]))
    }
  }, [dispatch, filterQueries])

  const estimatedFilter = useCallback(({ input, op }) => {
    const validOp = VALID_OPS_ESTIMATED.find((v) => v.op === op)
    const included = filterQueries.filter(({ key }) => key !== 'estimate_value')
    if (!validOp) {
      dispatch(redux.actions.ticket.setFilterQueries(included))
      return
    }
    if (!input) {
      dispatch(redux.actions.ticket.setFilterQueries(included))
    } else {
      dispatch(redux.actions.ticket.setFilterQueries([...included, { key: 'estimate_value', value: input, op: validOp.op }]))
    }
  }, [dispatch, filterQueries])

  return (
    <Filterbar.Content>
      <Filterbar.Grouped border>
        <legend>{locale.assigned}</legend>
        <Filterbar.Column offset={1}>
          <All
            applyFilter={assignedToFilter} checked={isAll}
          />
          <FilterMine
            applyFilter={assignedToFilter} checked={isMine}
          />
        </Filterbar.Column>
        <Filterbar.Row offset={-0}>
          <FilterEmployee
            checked={isOther} defaultValue={filterByAssignedTo} applyFilter={assignedToFilter}
          />
        </Filterbar.Row>
      </Filterbar.Grouped>
      <Filterbar.Grouped>
        <Filterbar.Row offset={-1}>
          <FilterProject
            defaultValue={filterByProject}
            applyFilter={projectFilter}
          />
          <FilterEmployee label={locale.project_responsible} radio={false} defaultValue={filterByProjectResponsible} applyFilter={projectResponsibleFilter} />
        </Filterbar.Row>
      </Filterbar.Grouped>
      <Filterbar.Grouped>
        <Filterbar.Row offset={-1}>
          <FilterEmployee label={locale.contact_responsible} radio={false} defaultValue={filterByContactResponsible} applyFilter={contactResponsibleFilter} />
        </Filterbar.Row>
      </Filterbar.Grouped>
      <Filterbar.Grouped>
        <Filterbar.Row offset={1}>
          <FilterNumber options={['>', '<', '>=', '<=']} applyFilter={estimatedFilter} />
        </Filterbar.Row>
      </Filterbar.Grouped>
    </Filterbar.Content>
  )
}

const mapStateToProps = (state) => {
  const { ticket } = state
  const {
    items,
    limit,
    offset,
    orderBy,
    order,
    search
  } = ticket
  return {
    limit,
    offset,
    orderBy,
    order,
    search,
    locale: state.locale.strings,
    permissions: state.login.permissions,
    tickets: items,
    editItem: state.ticket.editItem,
    deleteItem: state.ticket.deleteItem,
    strings: state.locale.strings
  }
}

const mapDispatchToProps = (dispatch) => {
  const { actions: { ticket } } = redux
  return {
    onFavoriteItemClick: (t) => {
      dispatch(ticket.toggleFavorite(t))
    },
    onShowAddDialog: () => {
      dispatch(ticket.showAddTicketDialog())
    },
    onDeleteOk: (t) => {
      dispatch(ticket.hideDeleteTicketDialog())
      dispatch(ticket.deleteTicket(t))
    },
    onDeleteItemClick: (t) => {
      dispatch(ticket.showDeleteTicketDialog(t))
    },
    onDeleteCancel: () => {
      dispatch(ticket.hideDeleteTicketDialog())
    },
    getTickets: () => {
      dispatch(ticket.getTickets())
    },
    setOrder: (orderBy, order) => dispatch(ticket.setOrder(orderBy, order)),
    setLimit: (limit) => dispatch(ticket.setLimit(limit)),
    setOffset: (offset) => dispatch(ticket.setOffset(offset)),
    setSearch: (search) => dispatch(ticket.setSearch(search)),
    resetSearch: () => dispatch(ticket.resetSearch()),
    setFilterQueries: (filterQueries) => dispatch(ticket.setFilterQueries(filterQueries))
  }
}

function Tickets (props) {
  const {
    deleteItem,
    getTickets, limit,
    locale,
    offset,
    resetSearch,
    onDeleteCancel,
    onDeleteItemClick,
    onDeleteOk,
    onFavoriteItemClick,
    orderBy,
    order,
    permissions,
    setSearch,
    setFilterQueries,
    tickets,
    setOrder,
    setLimit,
    setOffset,
    search
  } = props
  const dispatch = useDispatch()
  const setPageSizeRef = useRef(() => {})

  const settings = storage.getPageSettings('ticket')
  const [expanded, setExpanded] = useState(settings.toolbarExpanded || false)

  function onSetExpanded (set) {
    storage.putPageSetting('ticket', { toolbarExpanded: !expanded })
    setExpanded(set)
  }

  const selectFilterQueries = useMemo(makeSelectorFilterQueries, [])
  const filterQueries = useSelector((state) => selectFilterQueries(state, 'ticket'))
  const [tagFilterAnchorEl, setTagFilterAnchorEl] = useState(null)
  const [tagFilterSearch, setTagFilterSearch] = useState('')
  const [tagDraftSelection, setTagDraftSelection] = useState([])
  const [tagMatchMode, setTagMatchMode] = useState(TAG_FILTER_MODE_OR)

  const selectedTagMatchMode = useMemo(() => {
    const modeFromFilters = (filterQueries || [])
      .find((q) => q.key === TAG_FILTER_KEY && q.op === TAG_FILTER_OP)?.matchMode

    return modeFromFilters === TAG_FILTER_MODE_AND
      ? TAG_FILTER_MODE_AND
      : TAG_FILTER_MODE_OR
  }, [filterQueries])

  const selectedTags = useMemo(() => {
    return (filterQueries || [])
      .filter((q) => q.key === TAG_FILTER_KEY && q.op === TAG_FILTER_OP)
      .map((q) => q.value)
      .filter((value) => typeof value === 'string' && value.length > 0)
  }, [filterQueries])

  const selectedAssignedValues = useMemo(() => {
    return (filterQueries || [])
      .filter((q) => q.key === ASSIGNED_FILTER_KEY && q.op === TAG_FILTER_OP)
      .map((q) => q.value)
      .filter((value) => typeof value === 'string' && value.length > 0)
  }, [filterQueries])

  const tagOptions = useMemo(() => {
    const unique = new Set()
    ;(tickets || []).forEach((ticket) => {
      ;(ticket?.tags || []).forEach((tag) => {
        const value = tag?.name
        if (typeof value === 'string' && value.trim() !== '') {
          unique.add(value.trim())
        }
      })
    })
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'sv'))
  }, [tickets])

  const assignedOptions = useMemo(() => {
    const unique = new Set()
    ;(tickets || []).forEach((ticket) => {
      const personName = ticket?.person_role && ticket.person_role.length > 0
        ? ticket.person_role[0]?.person_name
        : null
      if (typeof personName === 'string' && personName.trim() !== '') {
        unique.add(personName.trim())
      }
    })
    return Array.from(unique).sort((a, b) => a.localeCompare(b, 'sv'))
  }, [tickets])

  const filteredTagOptions = useMemo(() => {
    const query = tagFilterSearch.trim().toLowerCase()
    if (query === '') {
      return tagOptions
    }
    return tagOptions.filter((tag) => tag.toLowerCase().includes(query))
  }, [tagFilterSearch, tagOptions])

  const isTagFilterOpen = Boolean(tagFilterAnchorEl)

  const openTagFilter = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()
    setTagDraftSelection(selectedTags)
    setTagMatchMode(selectedTagMatchMode)
    setTagFilterSearch('')
    setTagFilterAnchorEl(event.currentTarget)
  }, [selectedTagMatchMode, selectedTags])

  const closeTagFilter = useCallback(() => {
    setTagFilterAnchorEl(null)
  }, [])

  const applyTagFilter = useCallback(() => {
    const retained = (filterQueries || []).filter((q) => q.key !== TAG_FILTER_KEY)
    const tagQueries = tagDraftSelection
      .filter((tag) => typeof tag === 'string' && tag.length > 0)
      .map((tag) => ({
        key: TAG_FILTER_KEY,
        value: tag,
        op: TAG_FILTER_OP,
        matchMode: tagMatchMode
      }))
    setFilterQueries([...retained, ...tagQueries])
    closeTagFilter()
  }, [closeTagFilter, filterQueries, setFilterQueries, tagDraftSelection, tagMatchMode])

  const applyAssignedFilter = useCallback((values) => {
    const retained = (filterQueries || []).filter((q) => q.key !== ASSIGNED_FILTER_KEY)
    const valueQueries = (values || [])
      .filter((value) => typeof value === 'string' && value.length > 0)
      .map((value) => ({ key: ASSIGNED_FILTER_KEY, value, op: TAG_FILTER_OP }))
    setFilterQueries([...retained, ...valueQueries])
  }, [filterQueries, setFilterQueries])

  const toggleTagDraftValue = useCallback((tag) => {
    setTagDraftSelection((prev) => {
      if (prev.includes(tag)) {
        return prev.filter((value) => value !== tag)
      }
      return [...prev, tag]
    })
  }, [])

  useEffect(() => {
    getTickets()
  }, [limit, offset, orderBy, order, search, filterQueries])
  const navigate = useNavigate()
  const manualSort = useCallback(({ id, isSorted, isSortedDesc }) => {
    if (isSorted && !isSortedDesc) {
      setOrder(id, 'desc')
    } else if (isSorted && isSortedDesc) {
      setOrder('', '')
    } else {
      setOrder(id, 'asc')
    }
  }, [setOrder])
  const onlyOpenTickets = filterQueries.find(({ key, value }) => key === 'ended_at' && value === null)
  const hasActiveFilters = useMemo(() => {
    const hasSearch = typeof search === 'string' && search.trim() !== ''
    const hasFilterQueries = Array.isArray(filterQueries) && filterQueries.length > 0
    return hasSearch || hasFilterQueries
  }, [filterQueries, search])
  const clearAllFiltersLabel = locale.clear_all_filters || 'Clear all filters'

  const toggleClosedProjectsFilter = useCallback(() => {
    const rest = filterQueries.filter(({ key, value }) => key !== 'ended_at' && value !== null)
    const isOnlySHowingOpen = filterQueries.find(({ key, value }) => key === 'ended_at' && value === null)
    if (isOnlySHowingOpen) {
      dispatch(redux.actions.ticket.setFilterQueries([...rest]))
    } else {
      dispatch(redux.actions.ticket.setFilterQueries([...rest, { key: 'ended_at', value: null, op: '=' }]))
    }
  }, [dispatch, filterQueries])

  const clearAllFilters = useCallback(() => {
    resetSearch()
    setFilterQueries([])
    setTagDraftSelection([])
    setTagFilterSearch('')
    closeTagFilter()
  }, [closeTagFilter, resetSearch, setFilterQueries])

  let initialSortBy = []
  if (orderBy) {
    initialSortBy = [{
      id: orderBy,
      desc: order === 'desc'
    }]
  }
  const transformedRows = useMemo(() => tickets.map((r) => {
    const getResponsibleName = (l) => l?.find((pr) => pr.role_internal_name === 'responsible')?.person_name ?? ''
    const row = {
      [locale.id]: r.id,
      [locale.description]: trimNewLineFromString(r.description),
      [locale.name]: r.name ?? '',
      [locale.project]: r.project_name ?? '',
      [`${locale.project}_${locale.id}`]: r.project_id,
      [locale.project_responsible]: getResponsibleName(r.project_person_role),
      // we shouldn't be matching on id but we do not expose the internal name here right now.
      [locale.assigned]: r.person_role?.find((pr) => pr.role_id === 1)?.person_name ?? ''
    }

    if (r.started_at) {
      row[locale.started_at] = dayjs(r.started_at).format('YYYY-MM-DD')
    } else {
      row[locale.started_at] = ''
    }

    if (r.expires_at) {
      row[locale.expires_at] = dayjs(r.expires_at).format('YYYY-MM-DD')
    } else {
      row[locale.expires_at] = ''
    }

    if (r.ended_at) {
      row[locale.finished] = dayjs(r.ended_at).format('YYYY-MM-DD')
    } else {
      row[locale.finished] = ''
    }

    return row
  }), [tickets, locale])

  const pagination = (
    <TablePagination
      limit={limit}
      offset={offset}
      onRowsPerPageChange={(e) => {
        setLimit(e.target.value)
        setPageSizeRef.current(e.target.value)
      }}
      onPageChange={(e, number) => setOffset(number * limit)}
    />
  )

  const { ticket: { canUpdate, canDelete, canCreate } } = permissions
  const columns = useMemo(() => {
    return [
      {
        Header: locale.name,
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          return (
            <Td
              {...cell.getCellProps()} left={
                <>
                  <CircularTickHolder width={16} height={16} />
                  <NameHolder>
                    {!!original.name && <InternalLink to={`edit?id=${original.id}`}><Strikethrough isDone={original?.ended_at}>{original.name}</Strikethrough></InternalLink>}
                  </NameHolder>
                </>
              }
            />
          )
        },
        canSort: true,
        id: 'name',
        size: 'md',
        manualSort,
        sortType: () => {}
      },
      {
        id: 'person_name',
        Header: (
          <ColumnMultiSelectFilter
            title={locale.assigned}
            options={assignedOptions}
            selectedValues={selectedAssignedValues}
            onApply={applyAssignedFilter}
            locale={locale}
          />
        ),
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          const person = original.person_role && original.person_role.length > 0 && original.person_role[0]
          return (<Td {...cell.getCellProps()} left={person && person.person_name} />)
        },
        isSortable: false
      },
      {
        Header: locale.project,
        accessor: 'project_name',
        manualSort,
        sortType: () => {}
      },
      {
        id: 'tags_string',
        Header: (
          <TagsHeaderHolder>
            <span>{locale.tags}</span>
            <IconButton
              size='small'
              onClick={openTagFilter}
              title={locale.filter || locale.tags}
            >
              <FilterListIcon
                fontSize='inherit'
                style={{ color: selectedTags.length > 0 ? '#64B5F6' : '#FFFFFF' }}
              />
            </IconButton>
          </TagsHeaderHolder>
        ),
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          const tags = original.tags && original.tags.length > 0 && sortArray(original.tags, 'name').slice(0, 2).map(({ color, id, name }) => {
            return <TagHolder key={id}><Tag textColor='#FFFFFF' color={color}>{name}</Tag></TagHolder>
          })
          return (<Td {...cell.getCellProps()} left={tags && tags} />)
        },
        canSort: true,
        sortType: () => {},
        manualSort
      },
      {
        id: 'expires_at',
        Header: locale.expires_at,
        manualSort,
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          const formattedDate = original.expires_at ? dayjs(original.expires_at).format('YYYY-MM-DD') : ''
          return (<Td {...cell.getCellProps()} left={<Expired date={formattedDate}>{formattedDate}</Expired>} />)
        },
        canSort: true,
        sortType: () => {}
      },
      {
        id: 'estimate_value',
        Header: locale.estimate_value,
        accessor: 'estimate_value',
        manualSort,
        sortType: () => {}
      },
      {
        id: 'toolbar',
        Header: locale.tool_belt,
        Cell: ({ cell }) => {
          const { row: { original } } = cell
          return (
            <Td
              {...cell.getCellProps()}
              left={
                <>
                  <Tooltip content={locale.favorite}>
                    <Link
                      onClickHandler={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onFavoriteItemClick(original)
                      }}
                    >
                      <FavoriteHolder>{original.is_favorite ? <FavoriteFilled color='#ca6eb6' width={20} height={20} /> : <Favorite color='#ca6eb6' width={20} height={20} />}</FavoriteHolder>
                    </Link>
                  </Tooltip>
                </>
              }
              right={canDelete && <Tooltip content={locale.delete}><Spacer><Link onClickHandler={() => { onDeleteItemClick(original) }}><DeleteItemIcon /></Link></Spacer></Tooltip>}
            />
          )
        },
        size: 'sm',
        isSortable: false
      }
    ]
  }, [locale, canUpdate, canDelete, manualSort, onFavoriteItemClick, openTagFilter, selectedTags, assignedOptions, selectedAssignedValues, applyAssignedFilter])
  return (
    <PageHolder>
      {deleteItem && <DeleteItem text={deleteItem.name || deleteItem.first_name} onOk={() => { onDeleteOk(deleteItem) }} onCancel={onDeleteCancel} />}
      <PageToolbar
        expanded={expanded}
        setExpanded={onSetExpanded}
        left={
        (
          <>
            <CircularTick width={20} height={20} /> <span>{locale.tasks}</span>
            {canCreate && <Link onClickHandler={() => navigate('/tickets/new')}><Add width={20} height={20} color='#447fb1' /></Link>}
            <CsvButton
              download={locale.tasks} keys={[
                locale.id,
                locale.name,
                locale.description,
                locale.project,
                `${locale.project}_${locale.id}`,
                locale.project_responsible,
                locale.assigned,
                locale.started_at,
                locale.expires_at,
                locale.finished
              ]} data={transformedRows}
            />
            <FilterFavoriteButton filterQueries={filterQueries} onFilterChange={setFilterQueries} />
            <Switch value={onlyOpenTickets} onChangeValue={toggleClosedProjectsFilter} disabled={false} />
            {hasActiveFilters && <ClearFiltersHolder><Tooltip content={clearAllFiltersLabel}><span><Link onClickHandler={clearAllFilters}><FilterListIcon style={{ color: '#d32f2f' }} /></Link></span></Tooltip></ClearFiltersHolder>}
          </>
        )
      } center={<SearchField resetSearch={resetSearch} value={search} onChange={setSearch} />}
      >
        <FilterList />
      </PageToolbar>
      <TableHolder $isExpanded={expanded}>
        <Table columnResizeStorageKey='ticket' columns={columns} data={tickets} initialPageSize={limit} initialSortBy={initialSortBy}>
          {({ setPageSize }) => {
            // Store table callback in a ref so we can call it from elsewhere
            setPageSizeRef.current = setPageSize
          }}
        </Table>
      </TableHolder>
      <Popover
        open={isTagFilterOpen}
        anchorEl={tagFilterAnchorEl}
        onClose={closeTagFilter}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableEnforceFocus
        disableAutoFocus
      >
        <TagFilterBody onClick={(e) => e.stopPropagation()}>
          <TextField
            fullWidth
            size='small'
            value={tagFilterSearch}
            placeholder={locale.search || 'Search'}
            onChange={(e) => setTagFilterSearch(e.target.value)}
          />
          <FormLabel style={{ marginTop: 8, display: 'block' }}>
            {locale.match_mode || 'Match mode'}
          </FormLabel>
          <RadioGroup
            row
            value={tagMatchMode}
            onChange={(e) => setTagMatchMode(e.target.value)}
          >
            <FormControlLabel
              value={TAG_FILTER_MODE_OR}
              control={<Radio size='small' />}
              label={locale.match_mode_or || locale.or || 'OR'}
            />
            <FormControlLabel
              value={TAG_FILTER_MODE_AND}
              control={<Radio size='small' />}
              label={locale.match_mode_and || locale.and || 'AND'}
            />
          </RadioGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={tagDraftSelection.length === 0}
                onChange={() => setTagDraftSelection([])}
              />
            }
            label={locale.all || 'All'}
          />
          <FormGroup style={{ maxHeight: 260, overflowY: 'auto' }}>
            {filteredTagOptions.map((tag) => (
              <FormControlLabel
                key={tag}
                control={
                  <Checkbox
                    checked={tagDraftSelection.includes(tag)}
                    onChange={() => toggleTagDraftValue(tag)}
                  />
                }
                label={tag}
              />
            ))}
          </FormGroup>
          <TagFilterActions>
            <Button size='small' variant='contained' onClick={applyTagFilter}>OK</Button>
          </TagFilterActions>
        </TagFilterBody>
      </Popover>
      <PageFooter>{pagination}</PageFooter>
    </PageHolder>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(Tickets)
